<template>
  <div class="pid-page">
    <header class="pid-hero">
      <div>
        <div class="pid-kicker">APPSFLYER PARTNERS</div>
        <h1>Partners</h1>
      </div>
      <a class="pid-back" href="/apps/script-tags/data-manager">Back to data manager</a>
    </header>

    <section class="pid-board">
      <form @submit="save">
        <div class="pid-grid pid-grid--head">
          <div>ID</div>
          <div class="pid-sortable" @click="sortByField('pid_text')">Partner Name</div>
          <div>PID</div>
          <div>Verbals</div>
          <div>User Name</div>
          <div>Password</div>
          <div>Actions</div>
        </div>

        <div
          v-for="row of rows"
          :key="row.id"
          class="pid-grid pid-grid--row"
          :class="{ 'is-editing': row.edit }"
        >
          <div><span class="pid-id">#{{ row.id }}</span></div>

          <div>
            <div v-if="!row.edit">{{ row.pid_text }}</div>
            <b-form-input
              v-else
              class="pid-input"
              type="text"
              v-model.trim="row.pid_text"
              placeholder="Partner Name"
            />
          </div>

          <div>
            <div v-if="!row.edit" class="pid-mono">{{ row.pid_value }}</div>
            <b-form-input
              v-else
              class="pid-input pid-input-mono"
              type="text"
              v-model.trim="row.pid_value"
              placeholder="PID"
            />
          </div>

          <div class="pid-verbals-cell">
            <div v-if="!row.edit" class="pid-verbal-tags">
              <span
                v-for="id in partnerVerbalIds(row.verbal_ids)"
                :key="id"
                class="pid-verbal-tag"
              >
                {{ getVerbalCode(id) }}
              </span>
              <span v-if="!partnerVerbalIds(row.verbal_ids).length" class="pid-verbal-empty">—</span>
            </div>
            <div v-else class="pid-verbal-edit">
              <div class="pid-verbal-tags">
                <span
                  v-for="id in partnerVerbalIds(row.verbal_ids)"
                  :key="id"
                  class="pid-verbal-tag pid-verbal-tag--removable"
                >
                  {{ getVerbalCode(id) }}
                  <button
                    type="button"
                    class="pid-verbal-remove"
                    @click="removeVerbalFromRow(row, id)"
                  >
                    ×
                  </button>
                </span>
                <span v-if="!partnerVerbalIds(row.verbal_ids).length" class="pid-verbal-empty">No verbals</span>
              </div>
              <select
                class="pid-verbal-add"
                :value="''"
                @change="addVerbalToRow(row, $event)"
              >
                <option value="">+ Add verbal</option>
                <option
                  v-for="v in availableVerbalsToAdd(row)"
                  :key="v.id"
                  :value="v.id"
                >
                  {{ v.code }}
                </option>
              </select>
            </div>
          </div>

          <div>
            <div v-if="!row.edit">{{ row.user_name }}</div>
            <b-form-input
              v-else
              class="pid-input"
              :disabled="!userHasPermission('admin')"
              type="text"
              v-model.trim="row.user_name"
              placeholder="User Name"
            />
          </div>

          <div>
            <div v-if="!row.edit" class="pid-mono">{{ row.password }}</div>
            <b-form-input
              v-else
              class="pid-input"
              :disabled="!userHasPermission('admin')"
              type="text"
              v-model.trim="row.password"
              placeholder="Password"
            />
          </div>

          <div class="pid-actions">
            <template v-if="!row.edit">
              <button
                v-if="isAdmin"
                class="pid-btn pid-btn--neutral"
                type="button"
                @click="editRow(row.id)"
              >
                Edit
              </button>
              <button
                v-if="isAdmin"
                class="pid-btn pid-btn--danger"
                type="button"
                @click="deleteEntity(row.id)"
              >
                Delete
              </button>
            </template>
            <template v-else>
              <button class="pid-btn pid-btn--ok" type="button" @click="saveEditRow(row.id)">
                Save
              </button>
              <button class="pid-btn pid-btn--neutral" type="button" @click="cancelEditRow(row.id)">
                Cancel
              </button>
            </template>
          </div>
        </div>

        <div v-if="isAdmin && showNewRow" class="pid-grid pid-grid--row pid-grid--new">
          <div><span class="pid-id">NEW</span></div>
          <div>
            <b-form-input class="pid-input" type="text" v-model="newRow.pid_text" placeholder="Partner Name" />
          </div>
          <div>
            <b-form-input class="pid-input pid-input-mono" type="text" v-model="newRow.pid_value" placeholder="PID" />
          </div>
          <div class="pid-verbals-cell">
            <div class="pid-verbal-edit">
              <div class="pid-verbal-tags">
                <span
                  v-for="id in partnerVerbalIds(newRow.verbal_ids)"
                  :key="'new-' + id"
                  class="pid-verbal-tag pid-verbal-tag--removable"
                >
                  {{ getVerbalCode(id) }}
                  <button
                    type="button"
                    class="pid-verbal-remove"
                    @click="removeVerbalFromRow(newRow, id)"
                  >
                    ×
                  </button>
                </span>
              </div>
              <select
                class="pid-verbal-add"
                :value="''"
                @change="addVerbalToRow(newRow, $event)"
              >
                <option value="">+ Add verbal</option>
                <option
                  v-for="v in availableVerbalsToAdd(newRow)"
                  :key="v.id"
                  :value="v.id"
                >
                  {{ v.code }}
                </option>
              </select>
            </div>
          </div>
          <div>
            <b-form-input
              class="pid-input"
              :disabled="!userHasPermission('admin')"
              type="text"
              v-model="newRow.user_name"
              placeholder="User Name"
            />
          </div>
          <div>
            <b-form-input
              class="pid-input"
              :disabled="!userHasPermission('admin')"
              type="text"
              v-model="newRow.password"
              placeholder="Password"
            />
          </div>
          <div class="pid-actions">
            <button class="pid-btn pid-btn--ok" type="submit">Add</button>
          </div>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      newRow: {
        id: 0,
        pid_text: '',
        pid_value: '',
        user_name: '',
        password: '',
        verbal_ids: [],
      },
      rows: [],
      allVerbals: [],
      showNewRow: true,
      isAdmin: true,
      editSnapshots: {},
      sortOptions: {
        sortOrder: 'asc',
        sortByField: 'pid_text',
      },
    };
  },
  async created() {
    const permList = await this.$store.state.user.perms;
    if (Object.values(permList).indexOf('bundles.admin') > -1) {
      this.isAdmin = true;
    }
    await this.loadVerbals();
    await this.getListData();
  },
  methods: {
    async loadVerbals() {
      const data = await this.$http.post(this.resources.Management.getDmVerbals, {});
      if (data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to load verbals');
        return;
      }
      this.allVerbals = data.body.data || [];
    },
    normalizeVerbalIds(verbalIds) {
      if (typeof verbalIds === 'string') {
        return verbalIds
          .split(',')
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !isNaN(id));
      }
      if (!verbalIds || !Array.isArray(verbalIds)) {
        return [];
      }
      return verbalIds
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));
    },
    normalizeRow(row) {
      row.edit = false;
      row.verbal_ids = this.partnerVerbalIds(row.verbal_ids);
      return row;
    },
    isGeneralVerbal(verbal) {
      return verbal.is_general === 1 || verbal.is_general === true;
    },
    nonGeneralVerbals() {
      return this.allVerbals.filter((v) => !this.isGeneralVerbal(v));
    },
    partnerVerbalIds(verbalIds) {
      return this.normalizeVerbalIds(verbalIds).filter((id) => {
        const verbal = this.allVerbals.find((v) => Number(v.id) === Number(id));
        return verbal && !this.isGeneralVerbal(verbal);
      });
    },
    getVerbalCode(id) {
      const verbal = this.allVerbals.find((v) => Number(v.id) === Number(id));
      return verbal ? verbal.code : id;
    },
    availableVerbalsForRow(row) {
      const taken = new Set();
      const rowId = row.id || 0;
      const currentIds = this.partnerVerbalIds(row.verbal_ids);

      this.rows.forEach((r) => {
        if (r.id !== rowId) {
          this.partnerVerbalIds(r.verbal_ids).forEach((id) => taken.add(id));
        }
      });

      if (rowId === 0) {
        this.partnerVerbalIds(this.newRow.verbal_ids).forEach((id) => taken.add(id));
      }

      return this.nonGeneralVerbals().filter((v) => {
        return !taken.has(Number(v.id)) || currentIds.includes(Number(v.id));
      });
    },
    availableVerbalsToAdd(row) {
      const current = this.partnerVerbalIds(row.verbal_ids);
      return this.availableVerbalsForRow(row).filter((v) => !current.includes(Number(v.id)));
    },
    addVerbalToRow(row, event) {
      const id = parseInt(event.target.value, 10);
      if (isNaN(id)) {
        return;
      }
      const current = this.normalizeVerbalIds(row.verbal_ids);
      if (!current.includes(id)) {
        row.verbal_ids = [...current, id];
      }
      event.target.value = '';
    },
    removeVerbalFromRow(row, id) {
      const targetId = Number(id);
      row.verbal_ids = this.normalizeVerbalIds(row.verbal_ids).filter((v) => v !== targetId);
    },
    buildPidPayload(row) {
      return {
        id: row.id,
        pid_text: row.pid_text,
        pid_value: row.pid_value,
        user_name: row.user_name,
        password: row.password,
        verbal_ids: this.partnerVerbalIds(row.verbal_ids),
      };
    },
    async save(e) {
      e.preventDefault();
      const validateRes = await this.validateRowData(this.newRow.pid_text, this.newRow.pid_value);
      if (validateRes !== '') {
        this.notifyError(validateRes);
        return;
      }
      const data = await this.$http.post(this.resources.Management.savePid, this.buildPidPayload(this.newRow));
      if (data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to save partner');
        return;
      }
      this.newRow.pid_text = '';
      this.newRow.pid_value = '';
      this.newRow.password = '';
      this.newRow.user_name = '';
      this.newRow.verbal_ids = [];
      await this.getListData();
    },
    async getListData() {
      const data = await this.$http.post(this.resources.Management.getPid, {});
      this.rows = (data.body.data || []).map((row) => this.normalizeRow(row));
      this.applySort();
    },
    async deleteEntity(id) {
      const data = await this.$http.post(this.resources.Management.dmDeletePid, {
        id: parseInt(id, 10),
      });
      if (data.body.res && data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to delete partner');
        return;
      }
      await this.getListData();
    },
    editRow(id) {
      this.showNewRow = false;
      this.rows.forEach((row) => {
        if (row.id === id) {
          this.editSnapshots[id] = {
            pid_text: row.pid_text,
            pid_value: row.pid_value,
            user_name: row.user_name,
            password: row.password,
            verbal_ids: [...this.partnerVerbalIds(row.verbal_ids)],
          };
          row.verbal_ids = this.partnerVerbalIds(row.verbal_ids);
          row.edit = true;
        } else {
          if (row.edit && this.editSnapshots[row.id]) {
            Object.assign(row, this.editSnapshots[row.id]);
            delete this.editSnapshots[row.id];
          }
          row.edit = false;
        }
      });
    },
    cancelEditRow(id) {
      this.showNewRow = true;
      this.rows.forEach((row) => {
        if (row.id === id) {
          if (this.editSnapshots[id]) {
            Object.assign(row, this.editSnapshots[id]);
            delete this.editSnapshots[id];
          }
          row.edit = false;
        }
      });
    },
    async saveEditRow(id) {
      const editRowData = this.rows.find((row) => row.id === id);
      if (!editRowData) {
        return;
      }

      const validateRes = await this.validateRowData(editRowData.pid_text, editRowData.pid_value);
      if (validateRes !== '') {
        this.notifyError(validateRes);
        return;
      }

      const payload = this.buildPidPayload(editRowData);
      const data = await this.$http.post(this.resources.Management.savePid, payload);
      if (data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to save partner');
        return;
      }

      delete this.editSnapshots[id];
      this.showNewRow = true;
      await this.getListData();
    },
    async validateRowData(dm_pid_text, dm_pid_value) {
      if (dm_pid_value === '') {
        return "PID value can't be empty";
      }
      if (!this.validateStr(dm_pid_value)) {
        return 'Please enter a valid PID value';
      }
      if (dm_pid_text === '') {
        return "PID name can't be empty";
      }
      if (!this.validateStr(dm_pid_text)) {
        return 'Please enter a valid PID name';
      }
      return '';
    },
    validateStr(str) {
      const re = /([A-Za-z0-9-_]+)/;
      return re.test(str);
    },
    sortByField(fieldName) {
      if (this.sortOptions.sortByField === fieldName) {
        this.sortOptions.sortOrder = this.sortOptions.sortOrder === 'desc' ? 'asc' : 'desc';
      } else {
        this.sortOptions.sortByField = fieldName;
        this.sortOptions.sortOrder = 'asc';
      }
      this.applySort();
    },
    applySort() {
      const fieldName = this.sortOptions.sortByField;
      const nonIntFields = ['pid_text'];

      if (!nonIntFields.includes(fieldName)) {
        if (this.sortOptions.sortOrder === 'desc') {
          this.rows.sort((a, b) => (parseInt(a[fieldName], 10) < parseInt(b[fieldName], 10) ? 1 : -1));
        } else {
          this.rows.sort((a, b) => (parseInt(a[fieldName], 10) > parseInt(b[fieldName], 10) ? 1 : -1));
        }
        return;
      }

      this.rows.forEach((row) => {
        row[fieldName] = row[fieldName].trim();
      });
      if (this.sortOptions.sortOrder === 'desc') {
        this.rows.sort((a, b) => (a[fieldName].toLowerCase() < b[fieldName].toLowerCase() ? 1 : -1));
      } else {
        this.rows.sort((a, b) => (a[fieldName].toLowerCase() > b[fieldName].toLowerCase() ? 1 : -1));
      }
    },
  },
};
</script>

<style scoped>
.pid-page {
  min-height: 100%;
  padding: 24px;
  color: #0f172a;
  background: #f8fafc;
}

.pid-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 18px;
}

.pid-kicker {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .5px;
  background: rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(15, 23, 42, 0.12);
}

.pid-hero h1 {
  margin: 8px 0 4px;
  font-size: 32px;
  line-height: 1.05;
  font-weight: 800;
}

.pid-back {
  color: #0ea5e9;
  text-decoration: none;
  border: 1px solid rgba(14, 165, 233, 0.28);
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(14, 165, 233, 0.08);
}

.pid-board {
  border-radius: 20px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(15, 23, 42, 0.10);
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.10);
  backdrop-filter: blur(10px);
}

.pid-grid {
  display: grid;
  grid-template-columns: 68px minmax(140px, 1fr) minmax(120px, 1fr) minmax(180px, 1.2fr) minmax(110px, 1fr) minmax(110px, 1fr) 185px;
  column-gap: 12px;
  align-items: center;
}

.pid-grid--head {
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: .4px;
  text-transform: uppercase;
  padding: 8px 14px 12px;
}

.pid-sortable {
  cursor: pointer;
}

.pid-grid--row {
  margin-bottom: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.pid-grid--row:hover {
  border-color: rgba(14, 165, 233, 0.35);
  box-shadow: 0 14px 32px rgba(14, 165, 233, 0.10);
}

.pid-grid--row.is-editing {
  border-color: rgba(14, 165, 233, 0.65);
  background: rgba(240, 249, 255, 0.95);
  box-shadow: 0 0 0 1px rgba(14, 165, 233, 0.18) inset, 0 16px 34px rgba(14, 165, 233, 0.12);
}

.pid-grid--new {
  border-style: dashed;
  border-color: rgba(34, 197, 94, 0.45);
  background: rgba(240, 253, 244, 0.92);
}

.pid-id {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.06);
}

.pid-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.pid-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pid-btn {
  border: 0;
  border-radius: 10px;
  padding: 7px 12px;
  font-weight: 700;
  color: #fff;
}

.pid-btn--ok {
  background: linear-gradient(135deg, #10b981, #22c55e);
}

.pid-btn--neutral {
  background: linear-gradient(135deg, #64748b, #475569);
}

.pid-btn--danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.pid-verbals-cell {
  min-width: 0;
}

.pid-verbal-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pid-verbal-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(14, 165, 233, 0.12);
  border: 1px solid rgba(14, 165, 233, 0.25);
  color: #0369a1;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.pid-verbal-tag--removable {
  padding-right: 6px;
}

.pid-verbal-remove {
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;
  cursor: pointer;
}

.pid-verbal-remove:hover {
  color: #ef4444;
}

.pid-verbal-empty {
  color: #94a3b8;
  font-size: 13px;
}

.pid-verbal-edit {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pid-verbal-add {
  width: 100%;
  min-height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: #fff;
  padding: 6px 10px;
  font-size: 13px;
}

:deep(.pid-input.form-control) {
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 1);
}

:deep(.pid-input.form-control:focus) {
  border-color: rgba(14, 165, 233, 0.85);
  box-shadow: 0 0 0 0.18rem rgba(14, 165, 233, 0.22);
}

@media (max-width: 1200px) {
  .pid-board {
    overflow-x: auto;
  }

  .pid-grid {
    min-width: 1100px;
  }
}
</style>
